import { deleteCache, getOrSetCacheWithHit } from "@sajilni/cache";
import { prisma, BrandingEntityType, Prisma } from "client";
import { invalidateEventPageCacheForEvent } from "../event/eventPageCache";

const DEFAULT_PRIMARY_COLOR = "#7F56D9";
const DEFAULT_LOGO =
  "https://3nahrfk4mnfktf1o.public.blob.vercel-storage.com/media-upload-4x76MaLpz6PW9bc8YHHwu8Ajol32Ae.png";

const brandingFieldSelect = {
  font: true,
  primaryColor: true,
  secondaryColor: true,
  logo: true,
  favIcon: true,
  appVersionMinIosNetworkingApp: true,
  appVersionMinAndroidNetworkingApp: true,
} as const;

const BRANDING_CACHE_TTL_SECONDS = 5 * 60;

const buildBrandingCacheKey = (
  entityType: BrandingEntityType,
  entityId: string
) => `branding:${entityType}:${entityId}`;

const invalidateBrandingCache = async (
  entityType: BrandingEntityType,
  entityId: string
) => {
  try {
    await deleteCache(buildBrandingCacheKey(entityType, entityId));
  } catch (error) {
    console.warn("[cache] branding invalidation failed (non-blocking)", error);
  }
};

export const createBranding = async ({
  entityId,
  entityType,
  data,
  userId,
}: {
  entityId: string;
  entityType: BrandingEntityType;
  data: Omit<Prisma.BrandingUncheckedCreateInput, "entityID" | "entityType">;
  userId: string;
}) => {
  const branding = await prisma.branding.create({
    data: {
      entityID: entityId,
      entityType,
      ...data,
      logo: data.logo
        ? data.logo
        : entityType === BrandingEntityType.WHITE_LABEL
        ? DEFAULT_LOGO
        : "",
    },
  });
  await invalidateBrandingCache(entityType, entityId);
  return branding;
};

export const updateBranding = async ({
  entityId,
  entityType,
  data,
  userId,
}: {
  entityId: string;
  entityType: BrandingEntityType;
  data: Omit<Prisma.BrandingUncheckedUpdateInput, "entityID" | "entityType">;
  userId: string;
}) => {
  // Convert update operations to plain values for create
  const createData = Object.entries(data).reduce((acc, [key, value]) => {
    acc[key] =
      value && typeof value === "object" && "set" in value ? value.set : value;
    return acc;
  }, {} as Record<string, any>);

  const branding = await prisma.branding.upsert({
    where: { entityID: entityId },
    update: data,
    create: {
      entityID: entityId,
      entityType,
      ...createData,
    },
  });
  await invalidateBrandingCache(entityType, entityId);

  if (entityType === BrandingEntityType.EVENT) {
    const event = await prisma.event.findUnique({
      where: { id: entityId },
      select: { slug: true },
    });
    await invalidateEventPageCacheForEvent(entityId, event?.slug);
  }

  return branding;
};

export const updateExistingBranding = async ({
  brandingId,
  entityId,
  entityType,
  data,
}: {
  brandingId: string;
  entityId: string;
  entityType: BrandingEntityType;
  data: Pick<
    Prisma.BrandingUpdateInput,
    "appVersionMinIosNetworkingApp" | "appVersionMinAndroidNetworkingApp"
  >;
}) => {
  const branding = await prisma.branding.update({
    where: { id: brandingId },
    data,
  });
  await invalidateBrandingCache(entityType, entityId);
  return branding;
};

export const deleteBranding = async ({
  brandingId,
  userId,
}: {
  brandingId: string;
  userId: string;
}) => {
  const branding = await prisma.branding.delete({
    where: { id: brandingId },
  });
  if (branding?.entityType && branding?.entityID) {
    await invalidateBrandingCache(branding.entityType, branding.entityID);
  }
  return branding;
};

export const getBranding = async ({
  entityId,
  entityType,
}: {
  entityId: string;
  entityType: BrandingEntityType;
}) => {
  const { data: branding } = await getOrSetCacheWithHit(
    buildBrandingCacheKey(entityType, entityId),
    async () => {
      let resolvedBranding;
      if (entityType === BrandingEntityType.WHITE_LABEL) {
        resolvedBranding = await prisma.branding.findUnique({
          where: { entityID: entityId },
          select: brandingFieldSelect,
        });
      } else if (entityType === BrandingEntityType.EVENT) {
        resolvedBranding = await getBrandingByEventIdFallback({
          eventId: entityId,
        });
      } else if (entityType === BrandingEntityType.ORGANIZATION) {
        resolvedBranding = await getBrandingByOrganizationIdFallback({
          organizationId: entityId,
        });
      }

      return {
        font: resolvedBranding?.font || "",
        primaryColor: resolvedBranding?.primaryColor || DEFAULT_PRIMARY_COLOR,
        secondaryColor:
          resolvedBranding?.secondaryColor || DEFAULT_PRIMARY_COLOR,
        logo: resolvedBranding?.logo || DEFAULT_LOGO,
        favIcon: resolvedBranding?.favIcon,
        appVersionMinIosNetworkingApp:
          resolvedBranding?.appVersionMinIosNetworkingApp,
        appVersionMinAndroidNetworkingApp:
          resolvedBranding?.appVersionMinAndroidNetworkingApp,
      };
    },
    { ttlSeconds: BRANDING_CACHE_TTL_SECONDS }
  );

  return branding;
};

export const getBrandingByEventIdFallback = async ({
  eventId,
}: {
  eventId: string;
}) => {
  try {
    const event = await prisma.event.findUnique({
      where: { id: eventId },
      select: {
        branding: { select: brandingFieldSelect },
        organizations: {
          take: 1,
          select: {
            branding: { select: brandingFieldSelect },
          },
        },
        whiteLabel: {
          select: {
            branding: { select: brandingFieldSelect },
          },
        },
      },
    });

    return {
      font:
        event?.branding?.font ||
        event?.organizations?.[0]?.branding?.font ||
        event?.whiteLabel?.branding?.font ||
        "",
      primaryColor:
        event?.branding?.primaryColor ||
        event?.organizations?.[0]?.branding?.primaryColor ||
        event?.whiteLabel?.branding?.primaryColor ||
        DEFAULT_PRIMARY_COLOR,
      secondaryColor:
        event?.branding?.secondaryColor ||
        event?.organizations?.[0]?.branding?.secondaryColor ||
        event?.whiteLabel?.branding?.secondaryColor ||
        DEFAULT_PRIMARY_COLOR,
      logo:
        event?.branding?.logo ||
        event?.organizations?.[0]?.branding?.logo ||
        event?.whiteLabel?.branding?.logo ||
        null,
      favIcon:
        event?.branding?.favIcon ||
        event?.organizations?.[0]?.branding?.favIcon ||
        event?.whiteLabel?.branding?.favIcon ||
        null,
      appVersionMinIosNetworkingApp:
        event?.branding?.appVersionMinIosNetworkingApp ||
        event?.organizations?.[0]?.branding?.appVersionMinIosNetworkingApp ||
        event?.whiteLabel?.branding?.appVersionMinIosNetworkingApp ||
        null,
      appVersionMinAndroidNetworkingApp:
        event?.branding?.appVersionMinAndroidNetworkingApp ||
        event?.organizations?.[0]?.branding
          ?.appVersionMinAndroidNetworkingApp ||
        event?.whiteLabel?.branding?.appVersionMinAndroidNetworkingApp ||
        null,
    };
  } catch (error) {
    console.error("Error in getBrandingWithFallback:", error);
    throw error;
  }
};

export const getBrandingByOrganizationIdFallback = async ({
  organizationId,
}: {
  organizationId: string;
}) => {
  try {
    const organization = await prisma.organization.findUnique({
      where: { id: organizationId },
      select: {
        branding: { select: brandingFieldSelect },
        whiteLabel: {
          select: {
            image: true,
            branding: { select: brandingFieldSelect },
          },
        },
      },
    });

    return {
      font:
        organization?.branding?.font ||
        organization?.whiteLabel?.branding?.font ||
        "",
      primaryColor:
        organization?.branding?.primaryColor ||
        organization?.whiteLabel?.branding?.primaryColor ||
        DEFAULT_PRIMARY_COLOR,
      secondaryColor:
        organization?.branding?.secondaryColor ||
        organization?.whiteLabel?.branding?.secondaryColor ||
        DEFAULT_PRIMARY_COLOR,
      logo:
        organization?.branding?.logo ||
        organization?.whiteLabel?.branding?.logo ||
        organization?.whiteLabel?.image ||
        null,
      favIcon:
        organization?.branding?.favIcon ||
        organization?.whiteLabel?.branding?.favIcon ||
        null,
      appVersionMinIosNetworkingApp:
        organization?.branding?.appVersionMinIosNetworkingApp ||
        organization?.whiteLabel?.branding?.appVersionMinIosNetworkingApp ||
        null,
      appVersionMinAndroidNetworkingApp:
        organization?.branding?.appVersionMinAndroidNetworkingApp ||
        organization?.whiteLabel?.branding?.appVersionMinAndroidNetworkingApp ||
        null,
    };
  } catch (error) {
    console.error("Error in getBrandingByOrganizationIdFallback:", error);
    throw error;
  }
};
